import React, { useState, useRef } from 'react';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import MarketplaceServices from '../services/MarketplaceServices';

const ImageUpload = ({ 
  label,
  value, 
  onChange, 
  error = null, 
  disabled = false,
  required = false,
  maxSizeMB = 3, // Máximo 3MB conforme API
  maxWidth = 1200,
  maxHeight = 400,
  quality = 0.8,
  acceptedFormats = ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'], // Conforme API
  helpText = null
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      setUploadError(null);

      // Validate file
      const validation = MarketplaceServices.validateImageFile(file, maxSizeMB);
      if (!validation.isValid) {
        setUploadError(validation.errors.join(', '));
        return;
      }

      // Check file format
      if (!acceptedFormats.includes(file.type)) {
        setUploadError(`Formato não suportado. Use: ${acceptedFormats.map(f => f.split('/')[1].toUpperCase()).join(', ')}`);
        return;
      }

      // Resize and convert to base64 for preview
      const resizedBase64 = await MarketplaceServices.resizeImage(
        file, 
        maxWidth, 
        maxHeight, 
        quality
      );

      // Return both the file and preview for the parent component
      onChange({
        file: file,
        preview: resizedBase64
      });
      
    } catch (error) {
      console.error('Erro no upload:', error);
      setUploadError('Erro ao processar a imagem. Tente novamente.');
    } finally {
      setUploading(false);
      // Clear the input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = () => {
    onChange(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const triggerFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <Form.Group className="mb-3">
      <Form.Label>
        {label} {required && '*'}
      </Form.Label>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedFormats.join(',')}
        onChange={handleFileSelect}
        disabled={disabled || uploading}
        style={{ display: 'none' }}
      />

      {/* Upload area */}
      <div className="marketplace-image-upload-container">
        {!value ? (
          // Upload area
          <div 
            className={`marketplace-image-upload-area border rounded p-4 text-center ${
              disabled ? 'bg-light' : 'bg-white'
            } ${error ? 'border-danger' : 'border-secondary'}`}
            style={{ 
              cursor: disabled || uploading ? 'not-allowed' : 'pointer',
              minHeight: '200px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center'
            }}
            onClick={disabled || uploading ? undefined : triggerFileSelect}
          >
            {uploading ? (
              <div>
                <Spinner animation="border" variant="primary" className="mb-3" />
                <div className="text-muted">Processando imagem...</div>
              </div>
            ) : (
              <div>
                <i className="las la-cloud-upload-alt text-muted" style={{ fontSize: '3rem' }} />
                <div className="mt-2">
                  <div className="fw-bold">Clique para selecionar uma imagem</div>
                  <div className="small text-muted mt-1">
                    ou arraste e solte aqui
                  </div>
                </div>
                <div className="small text-muted mt-2">
                  Formatos aceitos: {acceptedFormats.map(f => f.split('/')[1].toUpperCase()).join(', ')}
                  <br />
                  Tamanho máximo: {maxSizeMB}MB
                  <br />
                  Dimensões recomendadas: {maxWidth}x{maxHeight}px
                </div>
              </div>
            )}
          </div>
        ) : (
          // Preview area
          <div className="marketplace-image-preview position-relative">
            <img
              src={value?.preview || value}
              alt="Preview"
              className="img-fluid rounded border"
              style={{ 
                maxWidth: '100%', 
                maxHeight: '200px', 
                objectFit: 'cover',
                display: 'block'
              }}
            />
            
            {/* Remove button */}
            <Button
              variant="danger"
              size="sm"
              className="position-absolute top-0 end-0 m-2"
              onClick={handleRemove}
              disabled={disabled || uploading}
              title="Remover imagem"
            >
              <i className="las la-times" />
            </Button>

            {/* Change button */}
            <div className="mt-2 text-center">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={triggerFileSelect}
                disabled={disabled || uploading}
              >
                <i className="las la-edit me-1" />
                Alterar Imagem
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Error display */}
      {(error || uploadError) && (
        <div className="invalid-feedback d-block mt-2">
          {error || uploadError}
        </div>
      )}

      {/* Help text */}
      {helpText && (
        <Form.Text className="text-muted">
          <i className="las la-info-circle me-1" />
          {helpText}
        </Form.Text>
      )}
    </Form.Group>
  );
};

export default ImageUpload;